import * as React from 'react';

import { Refresh } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
  Typography,
} from '@mui/material';
import httpLaunchRscript from 'consumers/httpLaunchRscript';
import { useSelector } from 'react-redux';
import selectGgEnvironment from 'selectors/ggEnvironment';
import selectSessionToken from 'selectors/sessionToken';
import selectTokenRoles from 'selectors/sessionTokenRoles';
import { jobDefinition } from 'utils/jobDefinition';
import { hasSuperAdminRole } from 'utils/roleUtils';

/**
 * Kick off a product lookup refresh for this version.
 *
 * A product only enters the model when a training run exports it, so anything
 * reviewed since has no lookup row and cannot anchor or join a norm set.
 * `refresh_product_export.R` fills that gap without a retrain: it cold-starts
 * the missing products against the frozen universe signatures and writes them
 * into the deployed job's rows. No new model, only new products in an existing
 * one.
 *
 * conchBatch runs it on AWS Batch and only accepts a superadmin, so anyone else
 * is given the command rather than a button that would 403.
 */

const SCRIPT_PATH = 'batchjobs/refresh_product_export.R';

const EXPLAINER =
  'Adds products reviewed since the last training run, without retraining. A product needs at least 10 reviews to be placed.';

type Props = { jobId?: number };

const RefreshLookupButton: React.FC<Props> = ({ jobId }) => {
  const [open, setOpen] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [batchJobId, setBatchJobId] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const token = useSelector((state) => selectSessionToken(state));
  const roles = useSelector((state) => selectTokenRoles(state));
  const allowed = hasSuperAdminRole(roles);
  // Which Batch definition runs the script, so a dev console can never
  // dispatch onto production compute. Read from the store rather than
  // useGgEnvironment: the same value, already fetched once at login, without
  // giving this button a GraphQL dependency.
  const environment = useSelector((state) => selectGgEnvironment(state));

  // Pinned to the version on screen, so the refresh cannot silently target a
  // different model than the one being looked at.
  const args = jobId ? [`--job_id=${jobId}`] : [];
  const command = `Rscript ${SCRIPT_PATH}${jobId ? ` --job_id=${jobId}` : ''}`;

  const onOpen = () => {
    setBatchJobId(null);
    setError(null);
    setOpen(true);
  };

  const onSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const result = await httpLaunchRscript(SCRIPT_PATH, args, token, {
        jobDefinition: environment ? jobDefinition(environment) : undefined,
      });
      setBatchJobId(result.job_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* describeChild: without it MUI makes the tooltip the button's
          accessible name, so screen readers lose "Refresh" entirely. */}
      <Tooltip title={EXPLAINER} describeChild>
        <span>
          <Button
            size="small"
            startIcon={<Refresh />}
            onClick={onOpen}
            disabled={!jobId}
            sx={{ textTransform: 'none', flexShrink: 0 }}
          >
            Refresh
          </Button>
        </span>
      </Tooltip>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Refresh this model&rsquo;s products</DialogTitle>
        <DialogContent>
          <DialogContentText gutterBottom>{EXPLAINER}</DialogContentText>

          {allowed ? (
            <DialogContentText variant="body2">
              This runs on AWS Batch and takes a while. It adds to the deployed
              model rather than making a new one, so nothing here changes
              version — reload once it finishes.
            </DialogContentText>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 1 }}>
                Starting a refresh needs superadmin. Ask someone who has it, or
                run it directly:
              </Alert>
              <Box
                component="code"
                sx={{
                  display: 'block',
                  px: 1,
                  py: 0.5,
                  backgroundColor: 'rgba(0,0,0,0.06)',
                  borderRadius: 1,
                  fontSize: '0.8rem',
                }}
              >
                {command}
              </Box>
            </>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}

          {batchJobId && (
            <Alert severity="success" sx={{ mt: 1 }}>
              Refresh started — AWS Batch job {batchJobId}.
              <Typography
                variant="caption"
                sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}
              >
                Nothing changes here until it finishes; reload then.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>
            {batchJobId ? 'Close' : 'Cancel'}
          </Button>
          {allowed && !batchJobId && (
            <Button
              variant="contained"
              onClick={onSubmit}
              disabled={submitting || !jobId}
            >
              {submitting ? 'Starting…' : 'Start refresh'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RefreshLookupButton;
